import React from 'react';
import { Link } from 'react-router-dom';
import Backdrop from './Dialog';
import StatusPill from './StatusPill';
import { formatCompactNumber, formatDateTime, formatDuration, humanize } from '../format';

/**
 * The summary behind a click on a crawl job row.
 *
 * Every field here already exists on the job record the list itself
 * fetched — `services/mira/crawl_worker.py` writes discovered/extracted/
 * saved/deactivated/failed/errors and all three timestamps onto the row as
 * the crawl runs. Nothing about this panel is a new capability on the
 * backend; it is a place to actually read what was already there instead of
 * a truncated error string in a table cell.
 *
 * `job` may be a plain row from the list (fast, no request) or the enriched
 * response from `GET /ops/crawl/jobs/{id}` (used for a deep link whose job
 * isn't in whatever page of the list happened to be loaded) — both shapes
 * are the same job record, so this renders either without caring which.
 */
function secondsBetween(a, b) {
  if (!a || !b) return null;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Number.isNaN(ms) ? null : ms / 1000;
}

function TimelineStep({ label, at, tone = 'neutral', durationAfter }) {
  const dotCls = { neutral: 'bg-baseline', good: 'bg-status-good', critical: 'bg-status-critical', info: 'bg-accent' }[tone];
  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col items-center pt-0.5">
        <span className={`h-2.5 w-2.5 rounded-full ${at ? dotCls : 'bg-hairline'}`} aria-hidden="true" />
        {durationAfter != null && <span className="mt-1 h-6 w-px bg-hairline" aria-hidden="true" />}
      </div>
      <div className="min-w-0 flex-1 pb-4 last:pb-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-medium text-ink">{label}</span>
          <span className="text-[11px] text-ink-muted">{at ? formatDateTime(at) : 'did not happen'}</span>
        </div>
        {durationAfter != null && (
          <p className="mt-0.5 text-[11px] text-ink-muted">{formatDuration(durationAfter)} until the next step</p>
        )}
      </div>
    </div>
  );
}

function FunnelStat({ label, value, tone = 'neutral' }) {
  const cls = { neutral: 'text-ink', good: 'text-status-good', critical: 'text-status-critical', warning: 'text-[#8a5a00]' }[tone];
  return (
    <div>
      <div className="text-[11px] text-ink-muted">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${cls}`}>{formatCompactNumber(value ?? 0)}</div>
    </div>
  );
}

export default function JobSummaryPanel({ job, onClose, loading = false }) {
  if (!job && !loading) return null;

  const result = job?.result || null;
  const queueWaitS = secondsBetween(job?.created_at, job?.started_at);
  const runTimeS = secondsBetween(job?.started_at, job?.finished_at);
  const isTerminal = job && ['succeeded', 'failed'].includes(job.status);

  return (
    <Backdrop onClose={onClose} labelledBy="job-summary-title" maxWidth="max-w-xl">
      {loading || !job ? (
        <p className="py-6 text-center text-sm text-ink-muted">Loading…</p>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 id="job-summary-title" className="truncate text-base font-semibold text-ink">
                {job.source_name || job.source_id}
              </h2>
              <p className="mt-0.5 truncate font-mono text-[11px] text-ink-muted">{job.job_id}</p>
            </div>
            <StatusPill status={job.status} />
          </div>

          {job.brand_id && (
            <Link
              to={`/brand/${encodeURIComponent(job.brand_id)}`}
              onClick={onClose}
              className="mt-2 inline-block text-xs font-medium text-raspberry-600 hover:text-raspberry-700"
            >
              Open this brand →
            </Link>
          )}

          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-y border-hairline py-3 text-xs sm:grid-cols-4">
            <div>
              <dt className="text-ink-muted">Trigger</dt>
              <dd className="mt-0.5 text-ink">
                {job.kind === 'force_all_member' ? 'Force-all' : humanize(job.trigger || 'scheduled')}
              </dd>
            </div>
            <div>
              <dt className="text-ink-muted">By</dt>
              <dd className="mt-0.5 truncate font-mono text-[11px] text-ink">{job.triggered_by || 'nightly job'}</dd>
            </div>
            {job.batch_id && (
              <div className="col-span-2">
                <dt className="text-ink-muted">Batch</dt>
                <dd className="mt-0.5 truncate font-mono text-[11px] text-ink">{job.batch_id}</dd>
              </div>
            )}
          </dl>

          <h3 className="mb-3 mt-4 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">Timeline</h3>
          <div>
            <TimelineStep label="Queued" at={job.created_at} tone="neutral" durationAfter={job.started_at ? queueWaitS : null} />
            <TimelineStep
              label="Started"
              at={job.started_at}
              tone={job.started_at ? 'info' : 'neutral'}
              durationAfter={isTerminal ? runTimeS : null}
            />
            <TimelineStep
              label={job.status === 'failed' ? 'Failed' : job.status === 'succeeded' ? 'Succeeded' : 'Finished'}
              at={job.finished_at}
              tone={job.status === 'failed' ? 'critical' : job.status === 'succeeded' ? 'good' : 'neutral'}
            />
          </div>

          {result && (
            <>
              <h3 className="mb-3 mt-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">What it did</h3>
              <div className="grid grid-cols-3 gap-3 rounded-xl border border-hairline bg-surface-page p-3 sm:grid-cols-6">
                <FunnelStat label="Discovered" value={result.discovered} />
                <FunnelStat label="Skipped (fresh)" value={result.skipped_fresh} />
                <FunnelStat label="Extracted" value={result.extracted} />
                <FunnelStat label="Saved" value={result.saved} tone="good" />
                <FunnelStat label="Deactivated" value={result.deactivated} tone="warning" />
                <FunnelStat label="Failed" value={result.failed} tone={result.failed ? 'critical' : 'neutral'} />
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">
                Discovered on the listing pages, minus ones already fresh from a recent crawl, is what got
                extracted; saved is what passed extraction cleanly. Deactivated is unrelated to this
                run's finds — it is everything previously seen that no longer appeared, delisted in the
                same pass.
              </p>
            </>
          )}

          {job.error && (
            <div className="mt-4 rounded-lg bg-status-critical/[0.06] px-3 py-2.5">
              <p className="text-xs font-semibold text-status-critical">Job-level failure</p>
              <p className="mt-1 text-xs leading-relaxed text-ink-secondary">{job.error}</p>
            </div>
          )}

          {result?.errors?.length > 0 && (
            <div className="mt-4">
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                Extraction errors ({result.errors.length})
              </h3>
              <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-hairline bg-surface-page p-2.5">
                {result.errors.slice(0, 50).map((err, i) => (
                  <li key={i} className="break-all font-mono text-[11px] text-ink-secondary">{err}</li>
                ))}
              </ul>
              {result.errors.length > 50 && (
                <p className="mt-1 text-[11px] text-ink-muted">+{result.errors.length - 50} more, not shown.</p>
              )}
            </div>
          )}

          {!isTerminal && !job.error && (
            <p className="mt-4 text-xs text-ink-muted">
              {job.status === 'running'
                ? 'Still running. The Crawl tab polls every 8 seconds, so this updates on its own.'
                : 'Waiting in the queue.'}
            </p>
          )}

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-hairline px-3 py-2 text-sm font-medium text-ink-secondary hover:bg-surface-page hover:text-ink"
            >
              Close
            </button>
          </div>
        </>
      )}
    </Backdrop>
  );
}
